import { createApp } from 'vue'
import { createPinia } from 'pinia'
import SuperTokens from 'supertokens-web-js'
import Session from 'supertokens-web-js/recipe/session'
import EmailPassword from 'supertokens-web-js/recipe/emailpassword'
import router from './router'
import App from './App.vue'
import './assets/main.css'
import { getDBJWT as getDBJWTUtil } from './utils/api'
import proxyUtils from './utils/proxy'
import i18n from './i18n'
import { initTelemetry } from './services/telemetryService'
import { installConsoleGate } from './composables/useDebugMode'
import { usePostHog } from './composables/usePostHog'
import { resetAllSessionState } from './utils/sessionReset'
import type { SessionWipeReason } from './utils/sessionWipeReason'
import { installBootWatchdog } from './utils/bootWatchdog'
import { brand } from './config/brand'

// Reflect the build-time brand in the tab title regardless of the static
// index.html default (ADG). Awagenta builds (VITE_BRAND=awagenta) read as
// "Awagenta EventHub"; the SuperTokens appName below follows the same source.
if (typeof document !== 'undefined') document.title = brand.appTitle

// Mute console.log/info/debug by default; flips on once useDebugMode polls
// property_settings.debug=true. console.warn/error stay live.
installConsoleGate();

// `?reset=1` boot — escape hatch for stuck local sessions. Wipes the
// SPA's auth state, strips the param so refreshing doesn't re-enter the
// reset, then continues normal boot. Cheaper than asking the user to
// dig into DevTools → Application → Storage → Clear site data.
// `?reason=` (set by the boot-watchdog overlay) is deliberately KEPT so
// the login page can say why, and a screenshot of the URL is evidence.
if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('reset') === '1') {
  const reasonParam = new URLSearchParams(window.location.search).get('reason')
  const reason: SessionWipeReason = reasonParam === 'boot_timeout' ? 'boot_timeout' : 'manual_reset'
  void resetAllSessionState(reason).finally(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete('reset')
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash)
  })
}

(window as any).getDBJWT = getDBJWTUtil;
(window as any).$utils = proxyUtils

SuperTokens.init({
  appInfo: {
    appName: import.meta.env.VITE_SUPERTOKENS_APP_NAME || brand.appTitle,
    apiDomain: import.meta.env.VITE_SUPERTOKENS_API_DOMAIN,
    apiBasePath: import.meta.env.VITE_SUPERTOKENS_API_BASE_PATH || '/auth'
  },
  recipeList: [
    Session.init({
      sessionExpiredStatusCode: 401,
      tokenTransferMethod: (import.meta.env.VITE_SUPERTOKENS_TOKEN_METHOD as "cookie" | "header") || "cookie",
      // When SuperTokens' own refresh fails (session expired/revoked while the
      // tab is open), surface a clean re-login instead of silent 401s and a
      // stuck UI. The Entra provider already has a watchdog for this; SuperTokens
      // relied on transparent SDK refresh, which leaves nothing to catch a failed
      // refresh. Guarded on sessionExpiredOrRevoked so it never fires on the
      // happy path. Dynamic import avoids a boot-time circular dep with the auth
      // store — this callback only runs long after Pinia is mounted.
      onHandleEvent: (context) => {
        if (context.action === "UNAUTHORISED" && context.sessionExpiredOrRevoked) {
          void import('./stores/auth').then(({ useAuthStore }) => {
            try { useAuthStore().handleSessionExpired('st_unauthorised') } catch { /* Pinia not ready */ }
          })
        }
      },
    }),
    EmailPassword.init()
  ]
})

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(i18n)

initTelemetry()

// Global exception capture into PostHog. Three layers:
//
//   1. Vue's app.config.errorHandler — render/setup/lifecycle errors
//      raised inside a component. Vue swallows these by default after
//      logging to console; we forward them so they surface in PostHog
//      with component context.
//
//   2. window.onerror — any synchronous runtime error not caught by Vue
//      (third-party scripts, async callbacks, etc.).
//
//   3. window.onunhandledrejection — Promise rejections with no `.catch`.
//      These are the most common silent failures in async-heavy code.
//
// Each handler is best-effort: PostHog itself failing must not block the
// app, so we wrap in try/catch and let the original handlers (if any)
// keep working. We deliberately use the composable lazily inside the
// handler so PostHog has had a chance to initialise via the router /
// auth flow before the first error fires.
const ph = usePostHog()

app.config.errorHandler = (err, instance, info) => {
  try {
    ph.trackError('vue_render', err, {
      vue_info: info,
      component_name: (instance as any)?.$options?.__name || (instance as any)?.$options?.name || undefined,
    })
  } catch { /* never block on telemetry */ }
  // Preserve dev visibility — Vue normally console.errors these.
  console.error('[Vue errorHandler]', err, info)
}

window.addEventListener('error', (event) => {
  try {
    ph.trackError('window_error', event.error || event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  } catch { /* never block on telemetry */ }
})

window.addEventListener('unhandledrejection', (event) => {
  try {
    ph.trackError('unhandled_promise', event.reason, {})
  } catch { /* never block on telemetry */ }
})

// ── Boot watchdog ────────────────────────────────────────────────
// If the router hasn't completed its first navigation within the budget
// (auth probe + permissions load = two network round trips), show a
// "still connecting" overlay with Reload / Reset-session buttons and let
// the boot keep going. It removes itself when the router completes.
//
// It used to force `/login?reset=1` after 5s, which wiped the Entra
// session from the SHARED localStorage — so a slow boot in one tab (a
// discarded tab waking up, a reload while the connection was settling)
// logged every other tab out as "expired in background". A slow boot is
// not a dead session; wiping is now the user's choice, never the timer's.
// See utils/bootWatchdog.ts.
const bootWatchdog = installBootWatchdog({ router })

// Mount with a safety net — if the initial render throws, log the error
// and surface the boot-watchdog overlay immediately (its Reload / Reset
// buttons are the recovery) instead of leaving a frozen white screen.
try {
  app.mount('#app')
} catch (mountErr) {
  console.error('[boot] app.mount threw — showing the boot-watchdog overlay', mountErr)
  try { ph.trackError('boot_mount', mountErr, {}) } catch { /* no-op */ }
  bootWatchdog.fireNow()
}
